<template>
    <div class="tct-container">
        <!-- Toolbar: open-trips indicator + free-text search + refresh. The dashboard only ever
             shows OPEN trips, so there is no status selector. -->
        <div class="tct-toolbar">
            <span class="tct-open-badge">Open Trips</span>
            <input
                v-model.trim="searchQuery"
                type="search"
                class="tct-search"
                placeholder="Search open trips..."
                aria-label="Search open trips"
            />
            <button
                class="tct-btn tct-btn-refresh"
                :disabled="loadingTable"
                @click="refreshTable"
            >
                {{ loadingTable ? 'Refreshing...' : 'Refresh Trips' }}
            </button>
        </div>

        <!-- Route / vendor filters. Optional; applied client-side over the open-trips result set
             so operators can narrow the list. -->
        <div class="tct-filters">
            <div class="tct-filter">
                <label class="tct-filter-label">Route</label>
                <input
                    v-model.trim="filterRoute"
                    type="text"
                    class="tct-filter-input"
                    placeholder="Route code"
                    @input="filterRoute = $event.target.value.toUpperCase()"
                />
            </div>
            <div class="tct-filter">
                <label class="tct-filter-label">Vendor</label>
                <input
                    v-model.trim="filterVendor"
                    type="text"
                    class="tct-filter-input"
                    placeholder="Vendor"
                />
            </div>
            <button
                v-if="hasActiveFilters"
                class="tct-btn tct-btn-clear"
                @click="clearFilters"
            >
                Clear Filters
            </button>
        </div>

        <!-- Open-trips table. -->
        <div class="tct-table-wrapper">
            <table class="tct-table">
                <thead>
                    <tr>
                        <th @click="setSort('TripSheetId')" class="tct-th-sortable">
                            Trip Sheet {{ sortIndicator('TripSheetId') }}
                        </th>
                        <th @click="setSort('routeCode')" class="tct-th-sortable">
                            Route {{ sortIndicator('routeCode') }}
                        </th>
                        <th @click="setSort('driverCode')" class="tct-th-sortable">
                            Driver {{ sortIndicator('driverCode') }}
                        </th>
                        <th @click="setSort('vendor')" class="tct-th-sortable">
                            Vendor {{ sortIndicator('vendor') }}
                        </th>
                        <th @click="setSort('TripDate')" class="tct-th-sortable">
                            Trip Date {{ sortIndicator('TripDate') }}
                        </th>
                        <th @click="setSort('costStatus')" class="tct-th-sortable">
                            Cost Status {{ sortIndicator('costStatus') }}
                        </th>
                        <th>Action</th>
                    </tr>
                </thead>
                <tbody>
                    <tr v-for="trip in filteredTrips" :key="trip.TripSheetId">
                        <td class="tct-td-code">{{ trip.TripSheetId }}</td>
                        <td>{{ trip.routeCode || '—' }}</td>
                        <td>{{ trip.driverCode || '—' }}</td>
                        <td>{{ trip.vendorName || trip.vendor || '—' }}</td>
                        <td>{{ trip.TripDate || '—' }}</td>
                        <td>
                            <span class="tct-status">{{ trip.costStatus || 'open' }}</span>
                        </td>
                        <td>
                            <button class="tct-btn tct-btn-review" @click="selectTrip(trip.TripSheetId)">
                                Review Cost
                            </button>
                        </td>
                    </tr>
                    <tr v-if="!filteredTrips.length">
                        <td colspan="7" class="tct-empty">
                            {{ loadingTable ? 'Loading...' : 'No trips.' }}
                        </td>
                    </tr>
                </tbody>
            </table>
        </div>

        <div class="tct-footer">
            <span class="tct-count">
                {{ filteredTrips.length }} of {{ tripData.length }} trip{{ tripData.length === 1 ? '' : 's' }}
            </span>
        </div>
    </div>
    <alert-user v-if="toggleAlertBox" @closeAlertBox="closeAlertBox($event)" :message="alertMessage"/>
</template>

<script>
// TripCostTable — open-trips list for the Trip Cost Approval dashboard (Task 15).
//
// Lists TripSheets filterable by cost status (open/approved/ignored/abandoned/all — applied
// server-side) plus route / trip date / vendor (applied client-side). Defaults to "open".
// The whole dashboard (route guard + host v-show) is group-gated to the BillingAdmin Cognito
// group (Admin retains access), so this table is only ever rendered for BillingAdmin users;
// selecting a trip emits selectTrip so the host can open the per-trip cost editor (Task 16+).
//
// Uses an inline listTripSheets query that omits createdAt/updatedAt to avoid AWSDateTime null
// errors (established codebase pattern), paging through nextToken to collect every open trip.

import { API } from "aws-amplify";
import AlertUser from "../../../components/Popups/AlertUser.vue";

export default {
    name: "TripCostTable",

    components: {
        AlertUser,
    },

    props: {
        jwtToken: { type: String, default: null },
    },

    emits: ["selectTrip"],

    data() {
        return {
            tripData: [],
            loadingTable: false,
            searchQuery: "",
            filterRoute: "",
            filterVendor: "",
            sortField: "TripSheetId",
            sortDirection: "asc",
            toggleAlertBox: false,
            alertMessage: "",
        };
    },

    computed: {
        hasActiveFilters() {
            return !!(this.filterRoute || this.filterVendor);
        },

        filteredTrips() {
            let list = this.tripData;

            // Free-text search across the key identity fields.
            if (this.searchQuery) {
                const q = this.searchQuery.toLowerCase();
                list = list.filter((t) =>
                    String(t.TripSheetId || "").toLowerCase().includes(q) ||
                    (t.routeCode || "").toLowerCase().includes(q) ||
                    (t.driverCode || "").toLowerCase().includes(q) ||
                    (t.vendor || "").toLowerCase().includes(q) ||
                    (t.vendorName || "").toLowerCase().includes(q)
                );
            }

            // Route filter.
            if (this.filterRoute) {
                const rc = this.filterRoute.toLowerCase();
                list = list.filter((t) => (t.routeCode || "").toLowerCase().includes(rc));
            }

            // Vendor filter (match either the Vendor PK or the denormalized name).
            if (this.filterVendor) {
                const v = this.filterVendor.toLowerCase();
                list = list.filter((t) =>
                    (t.vendor || "").toLowerCase().includes(v) ||
                    (t.vendorName || "").toLowerCase().includes(v)
                );
            }

            const field = this.sortField;
            const dir = this.sortDirection === "asc" ? 1 : -1;

            return [...list].sort((a, b) => {
                let valA = a[field];
                let valB = b[field];
                if (valA == null) valA = "";
                if (valB == null) valB = "";
                if (typeof valA === "number" && typeof valB === "number") return (valA - valB) * dir;
                return String(valA).localeCompare(String(valB)) * dir;
            });
        },
    },

    methods: {
        selectTrip(tripSheetId) {
            this.$emit("selectTrip", tripSheetId);
        },

        setSort(field) {
            if (this.sortField === field) {
                this.sortDirection = this.sortDirection === "asc" ? "desc" : "asc";
            } else {
                this.sortField = field;
                this.sortDirection = "asc";
            }
        },

        sortIndicator(field) {
            if (this.sortField !== field) return "";
            return this.sortDirection === "asc" ? "▲" : "▼";
        },

        clearFilters() {
            this.filterRoute = "";
            this.filterVendor = "";
        },

        closeAlertBox(toggleAlertBox) {
            this.toggleAlertBox = toggleAlertBox;
        },

        // Query OPEN TripSheets via the byCostStatus GSI (tripSheetByCostStatus), sorted by
        // openSince descending (newest first), paging through nextToken. This indexes directly on
        // costStatus = "open" instead of scanning the whole TripSheet table. The dashboard only
        // ever shows open trips — approved/ignored/abandoned are never queried here. Route/vendor
        // narrowing + free-text search are applied client-side via filteredTrips. Inline query
        // omits createdAt/updatedAt (AWSDateTime-null pattern).
        async listTrips() {
            this.loadingTable = true;
            const trips = [];
            let nextToken = null;

            const costStatus = "open";

            const graphqlQuery = /* GraphQL */ `
                query TripSheetsByCostStatus($costStatus: String!, $limit: Int, $nextToken: String) {
                    tripSheetByCostStatus(
                        costStatus: $costStatus,
                        sortDirection: DESC,
                        limit: $limit,
                        nextToken: $nextToken
                    ) {
                        items {
                            TripSheetId
                            routeCode
                            driverCode
                            vehicleCode
                            vendor
                            vendorName
                            costStatus
                            TripDate
                            SignOffDate
                            closedOn
                            openSince
                        }
                        nextToken
                    }
                }
            `;

            try {
                do {
                    const resp = await API.graphql({
                        query: graphqlQuery,
                        variables: { costStatus, limit: 1000, nextToken },
                        authMode: "AMAZON_COGNITO_USER_POOLS",
                    });

                    const payload = resp?.data?.tripSheetByCostStatus ?? {};
                    trips.push(...(payload.items ?? []));
                    nextToken = payload?.nextToken ?? null;
                } while (nextToken);

                this.tripData = trips;
            } catch (e) {
                console.log(e);
                this.toggleAlertBox = true;
                this.alertMessage = "Error getting trips";
            } finally {
                this.loadingTable = false;
            }
        },

        async refreshTable() {
            await this.listTrips();
        },
    },

    mounted() {
        this.listTrips();
    },
};
</script>

<style scoped>
.tct-container {
    width: 100%;
}

.tct-toolbar {
    display: flex;
    flex-direction: row;
    align-items: center;
    gap: 10px;
    margin-bottom: 10px;
    flex-wrap: wrap;
}

.tct-open-badge {
    background-color: #32ccfe;
    color: #fff;
    font-weight: bold;
    font-size: 13px;
    padding: 6px 14px;
    border-radius: 999px;
    white-space: nowrap;
}

.tct-search {
    padding: 8px 12px;
    font-size: 14px;
    border: 1px solid rgba(0, 0, 0, .336);
    border-radius: 15px;
    outline: none;
    flex: 1;
    min-width: 180px;
}

.tct-filters {
    display: flex;
    flex-direction: row;
    align-items: flex-end;
    gap: 10px;
    margin-bottom: 10px;
    flex-wrap: wrap;
}

.tct-filter {
    display: flex;
    flex-direction: column;
    align-items: flex-start;
}

.tct-filter-label {
    font-size: 12px;
    color: #6a6a6a;
    margin-bottom: 2px;
}

.tct-filter-input {
    padding: 6px 10px;
    font-size: 14px;
    border: 1px solid #e2e2e2;
    border-radius: 15px;
    box-shadow: rgba(0, 0, 0, .05) 0 5px 15px;
    outline: none;
}

.tct-table-wrapper {
    width: 100%;
    overflow-x: auto;
    border-radius: 5px;
}

.tct-table {
    width: 100%;
    border-collapse: collapse;
}

.tct-table thead th {
    background-color: #32ccfe;
    color: #fff;
    padding: 8px;
    text-align: left;
}

.tct-th-sortable {
    cursor: pointer;
    user-select: none;
}

.tct-table tbody td {
    background-color: #f3f3f3;
    padding: 8px;
    text-align: left;
    border-bottom: 5px solid #fff;
}

.tct-td-code {
    color: #6a6a6a;
}

.tct-status {
    background-color: #d4d4d4;
    padding: 2.5px 10px;
    border-radius: 999px;
    font-size: 12px;
}

.tct-empty {
    text-align: center;
    color: #6a6a6a;
}

.tct-footer {
    display: flex;
    flex-direction: row;
    justify-content: flex-end;
    align-items: center;
    margin-top: 5px;
}

.tct-count {
    font-size: 12px;
    color: #6a6a6a;
}

.tct-btn {
    cursor: pointer;
    border: 1px solid #a0a0a0;
    background-color: #f3f3f3;
    padding: 5px 12px;
    border-radius: 999px;
    transition-duration: .5s;
}

.tct-btn:hover {
    filter: brightness(0.9);
}

.tct-btn-refresh {
    background-color: #f3f3f3;
}

.tct-btn-clear {
    background-color: #fe804d;
    border-color: #ff5512;
    color: #fff;
}

.tct-btn-review {
    background-color: #32ccfe;
    border-color: #2baeda;
    color: #fff;
}
</style>
